const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

export type ResendByTokenStatus = 'sent' | 'already_verified' | 'unknown_token';

export interface ResendByTokenResponse {
  status: ResendByTokenStatus;
}

export interface ResendByEmailResponse {
  message: string;
}

export interface SubscribeResponse {
  subscribed: boolean;
}

export class RateLimitError extends Error {
  constructor() {
    super('Too many attempts, please try again in a minute.');
    this.name = 'RateLimitError';
  }
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (response.status === 429) {
    throw new RateLimitError();
  }

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  const contentType = response.headers.get('content-type');
  if (!contentType?.includes('application/json')) {
    const text = await response.text();
    throw new Error(
      `Expected JSON response but received ${contentType || 'unknown'}: ${text.substring(0, 100)}`
    );
  }

  return (await response.json()) as T;
}

export function resendVerificationByToken(
  token: string
): Promise<ResendByTokenResponse> {
  return postJson<ResendByTokenResponse>('/auth/resend-verification-by-token', {
    token,
  });
}

export function resendVerificationByEmail(
  email: string
): Promise<ResendByEmailResponse> {
  return postJson<ResendByEmailResponse>('/auth/resend-verification', {
    email,
  });
}

export function subscribe(email: string): Promise<SubscribeResponse> {
  return postJson<SubscribeResponse>('/users/subscribe', {
    email,
  });
}

const STORAGE_KEY_ACCESS_TOKEN = 'uk_vineyard_access_token';

export function getAccessToken(): string | null {
  return localStorage.getItem(STORAGE_KEY_ACCESS_TOKEN);
}

export function setAccessToken(token: string): void {
  localStorage.setItem(STORAGE_KEY_ACCESS_TOKEN, token);
}

export function clearTokens(): void {
  localStorage.removeItem(STORAGE_KEY_ACCESS_TOKEN);
}

export async function postJsonWithAuth<T>(
  path: string,
  body: unknown
): Promise<T> {
  const token = getAccessToken();
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
    body: JSON.stringify(body),
  });

  if (response.status === 401) {
    clearTokens();
    throw new Error('Session expired. Please log in again.');
  }

  if (response.status === 429) {
    throw new RateLimitError();
  }

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  const contentType = response.headers.get('content-type');
  if (!contentType?.includes('application/json')) {
    const text = await response.text();
    throw new Error(
      `Expected JSON response but received ${contentType || 'unknown'}: ${text.substring(0, 100)}`
    );
  }

  return (await response.json()) as T;
}
